/**
 * Property summary, through the property backend.
 *
 *   POST /image_summary   ask it to run    (ref_num, source_name)
 *   GET  /get_summary     poll for the result
 *
 * Both go through apiClient, so the Bearer token, the per-client host and the
 * 401 handling are already right.
 *
 * This replaced calling the Property Insights service directly, which could not
 * work from a browser for three separate reasons: the app had to read every
 * photograph's bytes and re-upload them as multipart, from a media host that
 * sends no CORS headers, to a plain-HTTP address on an internal network. The
 * backend already holds the images, so a ref_num is the whole request.
 *
 * NOT verified against a live response — this needs a property session, which
 * this workspace does not have. What `get_summary` returns *before* the result
 * exists is therefore unknown, so readSummaryResponse below treats every
 * plausible shape as "not ready yet" and only reports failure on the markers
 * this API family is documented to use. The first real run will say which of
 * them it actually is; it logs the raw body once for exactly that reason.
 */

import {
  triggerPropertySummary,
  fetchPropertySummary,
} from "./apiService";
import {
  readSummaryResponse,
  isNotYetStatus,
} from "../property/summaryResponse";

/** Per the service's own guide: 30 s to 3 min. Poll every few seconds. */
export const POLL_INTERVAL_MS = 4000;
export const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    });
  });

/**
 * `get_summary` answers 404 when there is no summary for this ref_num.
 *
 * The route itself exists — it returns 401 unauthenticated, like every other
 * route on that host — so a 404 with a valid token is "not generated yet", not
 * "no such endpoint". Treating it as an error was abandoning the poll on the
 * one status that means keep waiting.
 */
const NOT_YET = Symbol("summary-not-yet");

const fetchOrNotYet = async (refNum) => {
  try {
    return await fetchPropertySummary(refNum);
  } catch (error) {
    if (isNotYetStatus(error?.response?.status)) return NOT_YET;
    throw error;
  }
};

/** A summary the backend already holds, or null. No trigger, no waiting. */
export const readExistingSummary = async (refNum) => {
  const body = await fetchOrNotYet(refNum);
  if (body === NOT_YET) return null;

  const read = readSummaryResponse(body);
  if (read.state === "ready") return read.result;

  // Logged once, unlabelled shapes being the whole uncertainty here: this is
  // what says whether "not ready" looks the way this code assumes.
  console.info(
    "[Property] no summary yet for",
    refNum,
    "— response keys:",
    body && typeof body === "object" ? Object.keys(body) : typeof body,
    read.reason ? `(${read.reason})` : "",
  );
  return null;
};

/**
 * Ask for a summary and wait for it.
 *
 * Checks for an existing one first: the backend keeps them, so re-running is
 * work nobody needs and the model's answer would only change by chance.
 */
export const runPropertySummary = async (
  refNum,
  { onProgress, signal, force = false } = {},
) => {
  if (!refNum) throw new Error("runPropertySummary: ref_num is required");

  if (!force) {
    onProgress?.({ stage: "checking" });
    const existing = await readExistingSummary(refNum);
    if (existing) return existing;
  }

  onProgress?.({ stage: "starting" });
  const triggered = await triggerPropertySummary(refNum);

  // The trigger may answer with the summary itself when one is already held.
  const fromTrigger = readSummaryResponse(triggered);
  if (fromTrigger.state === "ready") return fromTrigger.result;
  if (fromTrigger.state === "failed") throw new Error(fromTrigger.reason);

  const deadline = Date.now() + POLL_TIMEOUT_MS;
  for (;;) {
    await wait(POLL_INTERVAL_MS, signal);

    const body = await fetchOrNotYet(refNum);
    if (body !== NOT_YET) {
      const read = readSummaryResponse(body);
      if (read.state === "ready") return read.result;
      if (read.state === "failed") throw new Error(read.reason);
    }

    if (Date.now() > deadline) {
      throw new Error(
        "The summary did not arrive within five minutes. The backend may still " +
          "be working — reopen the case to check.",
      );
    }

    onProgress?.({ stage: "running" });
  }
};
